/**
 * Component: Browse widget body-render building blocks
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R13, WIDGET-V1-R30
 *
 * Implements the BROWSE-V1 viewer behaviour the widget Browse
 * surface needs to render full guideline content (R04..R08)
 * inside an embedded host page. WIDGET-V1-R13 says the widget
 * shall expose a Browse component "providing access to the
 * BROWSE-V1 list and viewer surfaces" - the list lives in
 * browse.tsx, and the viewer (header + body + TOC + colour +
 * citations) lives here.
 *
 * Contents:
 *   - Narrowing helpers for the upstream fullContent payload
 *     (sectionContent, recommendationContent,
 *     recommendationColourOption, referenceContent). Mirrors
 *     the BROWSE-V1 frontend-web/types.ts narrowing shape.
 *   - SafeHtml: DOMPurify wrapper using the same allow-list
 *     as the portal's FRONTEND-WEB-V1 SafeHtml so a malicious
 *     upstream payload cannot inject scripts in either
 *     surface.
 *   - SectionContent: one entry from sectionContent[] with
 *     heading + sanitised HTML body + recommendation cards.
 *   - RecommendationCard: BROWSE-V1-R07 colour-coded
 *     recommendation block; colours come verbatim from the
 *     payload via CSS custom properties.
 *   - (The table of contents - BROWSE-V1-R06, WIDGET-V1-R28..R30
 *     - lives in browse-toc.tsx. Section anchors here carry a
 *     scroll margin so a TOC jump lands below the sticky
 *     contents bar.)
 *   - ReferencesList: BROWSE-V1-R08 references rendered as
 *     a numbered list under the body. Inline citation
 *     anchors in section bodies use href="#reference-{id}"
 *     and land on a matching <li id="reference-{id}"> via
 *     native browser scroll. Floating popovers and
 *     URL-deep-linking (R22) are out of scope for the
 *     widget surface; the embedded context does not own
 *     URL state.
 *
 * Risk Links: R7, R9
 * FMEA Links: FM7, FM9
 */

import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';


/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

/**
 * Subset of the BROWSE-V1 detail payload this module touches.
 * Defined as a structural type rather than imported from the
 * Browse surface so the helpers can be tested independently.
 */
export interface BrowseDetailLike {
  fullContent?: Record<string, unknown> | null;
}

/**
 * One entry from fullContent.sectionContent[]. Field names
 * mirror the BROWSE-V1 FRONTEND-WEB SectionContentEntry so
 * authors fluent in either viewer can read both.
 */
export interface SectionContentEntry {
  sectionId: number;
  sectionHierarchy: number;
  sectionName: string;
  /** Sanitised by SafeHtml at the consumer. */
  sectionText: string;
  /** Raw upstream entries; narrowed at consumer. */
  recommendationContent?: unknown[];
}

/**
 * BROWSE-V1-R07: One recommendation row inside a section.
 * The strength + evidence-strength colours come verbatim
 * from the upstream payload and are applied via CSS custom
 * properties on the rendered card.
 */
export interface RecommendationContentEntry {
  recommendationId: number;
  recommendationText: string;
  strength: { label: string; colour: string };
  evidenceStrength: { label: string; colour: string };
}

/**
 * BROWSE-V1-R08: One entry from fullContent.referenceContent[].
 * The referenceId is coerced to string so anchors with both
 * numeric upstream ids and string-typed payloads land on the
 * same lookup key.
 */
export interface ReferenceEntry {
  referenceId: string;
  referenceText: string;
  url?: string;
}

/* ------------------------------------------------------------------ */
/* Narrowing helpers                                                   */
/* ------------------------------------------------------------------ */

/**
 * BROWSE-V1-R05: Defensive narrowing for sectionContent[].
 * Drops malformed entries silently and preserves source order
 * (Array.prototype.filter is stable; consumers must not sort).
 * Returns [] when the key is missing or not an array, which the
 * widget surface renders as the "No section content" affordance.
 */
export function extractSectionContent(
  detail: BrowseDetailLike,
): SectionContentEntry[] {
  const raw = detail.fullContent?.['sectionContent'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: SectionContentEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    // Upstream emits the same id/name fields under both
    // section* and bare names depending on payload version.
    const rawId = e['sectionId'] ?? e['id'];
    const rawName = e['sectionName'] ?? e['name'];
    const rawHierarchy = e['sectionHierarchy'];
    const rawSortOrder = e['sectionSortOrder'];
    const rawText = e['sectionText'];
    const rawRecs = e['recommendationContent'];
    const sectionId = typeof rawId === 'number' ? rawId : null;
    // Derive hierarchy from sectionSortOrder dot-depth
    // (e.g. "9.6.5" -> 3) when sectionHierarchy is absent.
    const sectionHierarchy =
      typeof rawHierarchy === 'number'
        ? rawHierarchy
        : typeof rawSortOrder === 'string'
          ? rawSortOrder.split('.').length
          : null;
    const sectionName = typeof rawName === 'string' ? rawName : null;
    const sectionText = typeof rawText === 'string' ? rawText : '';
    if (sectionId === null || sectionHierarchy === null || sectionName === null) {
      continue;
    }
    narrowed.push({
      sectionId,
      sectionHierarchy,
      sectionName,
      sectionText,
      recommendationContent: Array.isArray(rawRecs) ? (rawRecs as unknown[]) : undefined,
    });
  }
  return narrowed;
}

/**
 * BROWSE-V1-R07: Narrow a section's recommendationContent[]
 * to entries with all required fields. Drops malformed
 * entries silently so a single broken upstream object can't
 * crash the rest of the section.
 */
export function extractRecommendationsFromSection(
  section: SectionContentEntry,
): RecommendationContentEntry[] {
  const raw = section.recommendationContent;
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: RecommendationContentEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const rawId = e['recommendationId'] ?? e['id'];
    const rawText = e['recommendationText'];
    const id = typeof rawId === 'number' ? rawId : null;
    const text = typeof rawText === 'string' ? rawText : null;
    const strength = narrowColouredLabel(e['strength']);
    const evidence = narrowColouredLabel(e['evidenceStrength']);
    if (id === null || text === null || !strength || !evidence) {
      continue;
    }
    narrowed.push({
      recommendationId: id,
      recommendationText: text,
      strength,
      evidenceStrength: evidence,
    });
  }
  return narrowed;
}

function narrowColouredLabel(
  raw: unknown,
): { label: string; colour: string } | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  // Upstream sometimes uses 'description' for the label.
  const label = o['label'] ?? o['description'];
  const colour = o['colour'];
  if (typeof label !== 'string' || typeof colour !== 'string') {
    return null;
  }
  return { label, colour };
}

/**
 * BROWSE-V1-R07: Read the top-level recommendationColourOption
 * mode selector. Forwarded verbatim onto every
 * RecommendationCard so a guideline renders consistently.
 */
export function extractRecommendationColourOption(
  detail: BrowseDetailLike,
): string | null {
  const raw = detail.fullContent?.['recommendationColourOption'];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * BROWSE-V1-R08: Defensive narrowing for referenceContent[].
 * Drops entries missing the two required fields. Returns []
 * when the key is missing or not an array.
 */
export function extractReferenceContent(
  detail: BrowseDetailLike,
): ReferenceEntry[] {
  const raw = detail.fullContent?.['referenceContent'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: ReferenceEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const rawId = e['referenceId'];
    const rawText = e['referenceText'];
    const rawUrl = e['url'];
    const referenceId =
      typeof rawId === 'number'
        ? String(rawId)
        : typeof rawId === 'string' && rawId.length > 0
          ? rawId
          : null;
    const referenceText = typeof rawText === 'string' ? rawText : null;
    if (referenceId === null || referenceText === null) {
      continue;
    }
    narrowed.push({
      referenceId,
      referenceText,
      url: typeof rawUrl === 'string' && rawUrl.length > 0 ? rawUrl : undefined,
    });
  }
  return narrowed;
}

/* ------------------------------------------------------------------ */
/* SafeHtml: DOMPurify wrapper                                         */
/* ------------------------------------------------------------------ */

/**
 * Allow-list mirrors the FRONTEND-WEB-V1 SafeHtml component
 * so the widget and the portal accept the same shape of
 * upstream guideline HTML. Anything else (script, iframe,
 * style, on* event handlers) is stripped by DOMPurify.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
  'strong', 'b', 'em', 'i', 'sup', 'sub', 'u', 'span',
  'a',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'figure', 'figcaption', 'img',
];

const ALLOWED_ATTR = [
  'src', 'alt', 'class', 'href', 'target', 'rel',
  // BROWSE-V1-R08: data-reference-id is the explicit
  // citation-id attribute upstream uses on inline anchors.
  'data-reference-id',
  // Upstream Bootstrap-style popover hints. We keep them in
  // the allow-list for parity with the portal SafeHtml; the
  // widget surface does not currently render popovers, so
  // these become inert visual hints.
  'data-content', 'data-toggle', 'data-placement',
];

// AB#4402 follow-up: clamp images in rendered guideline HTML so a
// natural-size figure from the source PDF (e.g. a title-page seal)
// can't overflow the content column. The source markup's `style`
// attribute is stripped during sanitisation (it's not in
// ALLOWED_ATTR); this hook re-adds a fixed, injection-safe
// constraint *after* sanitisation, plus drops any hard-coded
// width/height so the max-width clamp is honoured.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof Element && node.nodeName === 'IMG') {
    node.removeAttribute('width');
    node.removeAttribute('height');
    node.setAttribute('style', 'max-width:100%;height:auto');
  }
});

function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

interface SafeHtmlProps {
  html: string | null | undefined;
  style?: React.CSSProperties;
}

/**
 * Sanitised HTML render. Returns null when given empty input
 * so consumers can use it directly inside conditional JSX
 * without a wrapping check.
 */
export const SafeHtml: React.FC<SafeHtmlProps> = ({ html, style }) => {
  if (!html) return null;
  const clean = sanitizeHtml(html);
  return <div style={style} dangerouslySetInnerHTML={{ __html: clean }} />;
};

/* ------------------------------------------------------------------ */
/* RecommendationCard                                                  */
/* ------------------------------------------------------------------ */

interface RecommendationCardProps {
  recommendation: RecommendationContentEntry;
  /** Forwarded mode selector, exposed as data-rec-colour-mode. */
  colourOption: string | null;
}

/**
 * BROWSE-V1-R07: Render a recommendation row with the
 * upstream-supplied colours. The strength colour drives a
 * left accent border so the card is recognisable at a
 * glance in long guideline bodies; the strength + evidence
 * pills reuse the same palette.
 */
export const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  colourOption,
}) => {
  const cardStyle = {
    '--galen-rec-strength-colour': recommendation.strength.colour,
    '--galen-rec-evidence-colour': recommendation.evidenceStrength.colour,
    borderLeftColor: 'var(--galen-rec-strength-colour)',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    borderTop: '1px solid var(--galen-border, #e2e8f0)',
    borderRight: '1px solid var(--galen-border, #e2e8f0)',
    borderBottom: '1px solid var(--galen-border, #e2e8f0)',
    borderRadius: 'var(--galen-border-radius, 0.5rem)',
    background: 'var(--galen-card, #ffffff)',
    padding: '0.75rem',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
  } as React.CSSProperties;

  const pillBase: React.CSSProperties = {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    color: '#ffffff',
  };

  return (
    <article
      data-galen-recommendation
      data-galen-recommendation-id={recommendation.recommendationId}
      data-rec-colour-mode={colourOption ?? undefined}
      style={cardStyle}
    >
      <div style={{ marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span
          data-galen-recommendation-strength
          style={{ ...pillBase, background: 'var(--galen-rec-strength-colour)' }}
        >
          {recommendation.strength.label}
        </span>
        <span
          data-galen-recommendation-evidence
          style={{ ...pillBase, background: 'var(--galen-rec-evidence-colour)' }}
        >
          {recommendation.evidenceStrength.label}
        </span>
      </div>
      {recommendation.recommendationText && (
        <SafeHtml html={recommendation.recommendationText} />
      )}
    </article>
  );
};

/* ------------------------------------------------------------------ */
/* SectionContent                                                      */
/* ------------------------------------------------------------------ */

interface SectionContentProps {
  section: SectionContentEntry;
  colourOption: string | null;
}

type HeadingTag = 'h2' | 'h3' | 'h4';

function headingTagFor(hierarchy: number): HeadingTag {
  if (hierarchy <= 1) return 'h2';
  if (hierarchy === 2) return 'h3';
  return 'h4';
}

/**
 * BROWSE-V1-R05: Render one section. The DOM contract is
 * id="cnt_<sectionId>" so any deep-link mechanism (the
 * portal viewer's R22 hook, future widget extension) lands
 * on the same anchor names.
 */
export const SectionContent: React.FC<SectionContentProps> = ({
  section,
  colourOption,
}) => {
  const Heading = headingTagFor(section.sectionHierarchy);
  const recommendations = useMemo(
    () => extractRecommendationsFromSection(section),
    [section],
  );

  return (
    <section
      id={`cnt_${section.sectionId}`}
      data-galen-section
      data-galen-section-id={section.sectionId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.75rem 0',
        borderTop: '1px solid var(--galen-border, #e2e8f0)',
        // R30: a jump to this section (TOC, deep link) stops below the
        // sticky contents bar and any host fixed header.
        scrollMarginTop: 'calc(var(--galen-toc-top-offset, 0px) + 3.5rem)',
      }}
    >
      <Heading
        style={{
          margin: 0,
          fontSize: section.sectionHierarchy <= 1 ? '1.05rem' : '0.95rem',
          fontWeight: 600,
          color: 'var(--galen-foreground, #0f172a)',
          scrollMarginTop: '1rem',
        }}
      >
        {section.sectionName}
      </Heading>
      {section.sectionText && (
        <SafeHtml
          html={section.sectionText}
          style={{
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: 'var(--galen-foreground, #0f172a)',
          }}
        />
      )}
      {recommendations.length > 0 && (
        <div
          data-galen-section-recommendations
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.recommendationId}
              recommendation={rec}
              colourOption={colourOption}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* ReferencesList                                                      */
/* ------------------------------------------------------------------ */

interface ReferencesListProps {
  references: ReferenceEntry[];
}

/**
 * BROWSE-V1-R08 (widget-scoped): Render the guideline's
 * citation list at the bottom of the detail panel. Inline
 * anchors in section bodies that use href="#reference-{id}"
 * land on the matching <li id="reference-{id}"> via the
 * browser's native fragment-scroll behaviour - no popovers,
 * no positioning library. Anchors that use the upstream
 * Bootstrap-style data-toggle="popover" without an explicit
 * href are inert in the widget surface; the same anchors
 * open floating popovers in the portal viewer, which has
 * the Radix toolkit available.
 */
export const ReferencesList: React.FC<ReferencesListProps> = ({ references }) => {
  if (references.length === 0) {
    return null;
  }
  return (
    <section
      data-galen-references
      aria-labelledby="galen-references-heading"
      style={{
        marginTop: '1rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--galen-border, #e2e8f0)',
      }}
    >
      <h3
        id="galen-references-heading"
        style={{
          margin: 0,
          marginBottom: '0.5rem',
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--galen-foreground, #0f172a)',
        }}
      >
        References
      </h3>
      <ol
        style={{
          margin: 0,
          paddingLeft: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          fontSize: '0.85rem',
          color: 'var(--galen-foreground, #0f172a)',
        }}
      >
        {references.map((ref) => (
          <li
            key={ref.referenceId}
            id={`reference-${ref.referenceId}`}
            data-galen-reference
            data-galen-reference-id={ref.referenceId}
            style={{ scrollMarginTop: '1rem' }}
          >
            {ref.referenceText}
            {ref.url && (
              <>
                {' '}
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'var(--galen-primary, #0f172a)' }}
                >
                  ({ref.url})
                </a>
              </>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};
